







































import { computed, defineComponent, onBeforeUnmount, PropType, ref } from "@vue/composition-api";
import appStorage from "@/lib/appStorage";
import { MediaConstraints } from "@/types";
import BaseVideo from "@/components/BaseVideo";
import { StreamUser } from "../../types";

export const STORAGE_KEY = "live__custom_width";

interface UserToView {
  stream: MediaStream;
  constraints: MediaConstraints | null;
  isOwner: boolean;
  isLocal: boolean;
}

export default defineComponent({
  name: "UserStreamsView",
  components: { BaseVideo },
  props: {
    usersToConsume: { type: Array as PropType<StreamUser[]>, required: true },
    isProducing: { type: Boolean, required: true },
    constraints: { type: Object as PropType<MediaConstraints> },
    localStream: { type: MediaStream, required: true },
    isOwner: { type: Boolean, required: true },
    ownerId: { type: String, required: true },
  },
  setup(props) {
    const showStream = (user: StreamUser) => user.constraints?.video;
    const isUserOwner = (user: StreamUser) => user.user.userId === props.ownerId;

    const streamUserToView = computed(() => {
      const result: UserToView[] = [];
      if (props.isProducing) {
        result.push({
          stream: props.localStream,
          constraints: props.constraints ?? null,
          isOwner: props.isOwner,
          isLocal: true,
        });
      }
      result.push(
        ...props.usersToConsume.map(i => ({
          stream: i.stream,
          constraints: i.constraints,
          isOwner: isUserOwner(i),
          isLocal: false,
        }))
      );
      return result;
    });

    const hasSecondVideo = computed(
      () => streamUserToView.value.length > 1 && streamUserToView.value.every(i => i.constraints?.video)
    );
    const streamUserOwner = computed(() => streamUserToView.value.find(i => i.isOwner));
    const streamUserOpponent = computed(() => streamUserToView.value.find(i => !i.isOwner));

    const root = ref<HTMLElement | null>(null);
    const firstVideoCont = ref<HTMLElement | null>(null);
    const customWidth = ref<string>(appStorage.getString(STORAGE_KEY, ""));

    let dragging = false;
    let startX = 0;
    let startWidthA = 0;
    const minWidth = 190;

    const calculateWidth = (newWidthA: number) => {
      if (!root.value || !firstVideoCont.value) return;
      const containerWidth = root.value.getBoundingClientRect().width;
      const maxWidth = containerWidth - minWidth - 10;

      if (newWidthA < minWidth) {
        newWidthA = minWidth;
      }
      if (newWidthA > maxWidth) {
        newWidthA = maxWidth;
      }
      newWidthA = (newWidthA / containerWidth) * 100;
      customWidth.value = newWidthA.toFixed(2) + "%";
    };

    function onDragMove(event: MouseEvent | TouchEvent) {
      if (!dragging || !firstVideoCont.value || !root.value) return;

      const currentX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
      const delta = currentX - startX;
      let newWidthA = startWidthA + delta;
      calculateWidth(newWidthA);

      event.preventDefault();
    }

    function onDragEnd() {
      dragging = false;
      appStorage.setString(STORAGE_KEY, customWidth.value);
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("touchmove", onDragMove);
      window.removeEventListener("mouseup", onDragEnd);
      window.removeEventListener("touchend", onDragEnd);
    }

    function onDragStart(event: MouseEvent | TouchEvent) {
      dragging = true;
      startX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
      if (firstVideoCont.value) {
        startWidthA = firstVideoCont.value.getBoundingClientRect().width;
        window.addEventListener("mousemove", onDragMove);
        window.addEventListener("touchmove", onDragMove);
        window.addEventListener("mouseup", onDragEnd);
        window.addEventListener("touchend", onDragEnd);
      }
    }

    onBeforeUnmount(() => {
      onDragEnd();
    });

    return {
      root,
      firstVideoCont,
      hasSecondVideo,
      customWidth,
      streamUserOwner,
      streamUserOpponent,
      onDragStart,
      isUserOwner,
      showStream,
    };
  },
});
